import type { ReplicantAsyncActionAPI } from '../../api/ReplicantAPI';
import type { Replicant } from '../../ReplicantConfig';
import { serializeError } from '../../utils/ErrorUtils';
import { deepCopy } from '../../utils/ObjUtils';
import { isPromise } from '../../utils/Utils';

export function wrapAPI<T extends Replicant>(
    replicantAPI: ReplicantAsyncActionAPI<T>,
): {
    api: ReplicantAsyncActionAPI<T> & { hasUsedAsyncAPIs: boolean };
    results: { value: any; async: boolean; resolved: boolean; failed?: boolean }[];
} {
    const origApi = { ...replicantAPI };
    const outApi: ReplicantAsyncActionAPI<T> & { hasUsedAsyncAPIs: boolean } = {
        ...origApi,
        hasUsedAsyncAPIs: false,
    };

    const results: { value: any; async: boolean; resolved: boolean; failed?: boolean }[] = [];

    function wrap(obj: any, origAPI: any) {
        for (const key in obj) {
            if (typeof obj[key] === 'function') {
                obj[key] = ((...args: any[]) => {
                    let result: any;
                    try {
                        result = origAPI[key].apply(null, args);
                    } catch (error) {
                        results.push({
                            value: deepCopy(serializeError(error)),
                            async: false,
                            resolved: true,
                            failed: true,
                        });
                        throw error;
                    }

                    // this is a promise
                    if (isPromise(result)) {
                        const resultId = results.length;
                        results.push({ value: '', async: true, resolved: false }); // reserve the slot
                        result
                            .then((val) => {
                                results[resultId]!.value = deepCopy(val);
                                results[resultId]!.resolved = true;
                            })
                            .catch((error: any) => {
                                results[resultId]!.value = deepCopy(serializeError(error));
                                results[resultId]!.resolved = true;
                                results[resultId]!.failed = true;
                            });

                        outApi.hasUsedAsyncAPIs = true;
                    } else {
                        results.push({ value: deepCopy(result), async: false, resolved: true });
                    }

                    return result;
                }) as any;
            } else if (typeof obj[key] === 'object') {
                // TODO: Think about making this cleaner!
                obj[key] = { ...obj[key] }; // shallow copy as to not edit the APIs.

                wrap(obj[key], origAPI[key]);
            }
        }
    }

    wrap(outApi, origApi);

    return { api: outApi, results };
}
